<template>
  <div class="course-layout-v3">
    <Loading v-model:active="loadingStore.isLoading" :can-cancel="false" :is-full-page="true" />

    <!-- Top Navigation Bar -->
    <div class="course-header">
      <div class="header-content">
        <div class="header-left">
          <button class="mobile-menu-toggle" @click="toggleSidebar">
            <i class="fa fa-bars"></i>
          </button>
          <div class="course-info">
            <h1 class="course-title">{{ course?.title }}</h1>
          </div>
        </div>
        <div class="header-right">
          <router-link to="/caduceus/dashboard" class="btn-home">
            <i class="fa fa-th"></i>
            <span class="btn-text">Meus Cursos</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="course-content-wrapper" :class="{ 'sidebar-open': sidebarOpen }">
      <!-- Sidebar -->
      <aside class="course-sidebar" :class="{ 'mobile-open': sidebarOpen }">
        <div class="sidebar-header">
          <h3>Conteúdo do Curso</h3>
          <button class="close-sidebar" @click="toggleSidebar">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="sidebar-content">
          <!-- Course Modules -->
          <div class="modules-list">
            <template v-for="(module, moduleIndex) in course?.modules" :key="module.id">
              <div class="module-section">
                <button class="module-header" @click="toggleModule(moduleIndex)">
                  <div class="module-title-wrapper">
                    <i class="fas fa-chevron-down module-chevron" :class="{ 'expanded': expandedModules[moduleIndex] }"></i>
                    <span class="module-title">{{ module.title }}</span>
                  </div>
                  <span class="module-lesson-count">{{ getEnabledLessonsCount(module) }}</span>
                </button>

                <transition name="expand">
                  <div class="lessons-container" v-show="expandedModules[moduleIndex]">
                    <template v-for="(lesson, lessonIndex) in module.lessons" :key="lesson.id">
                      <button
                        v-if="lesson.isEnabled"
                        class="lesson-button"
                        :class="{ 'active': currentLesson.id === lesson.id }"
                        @click="selectLesson(module, lesson)"
                      >
                        <div class="lesson-checkbox">
                          <i class="fas fa-check-circle" v-if="currentLesson.id === lesson.id"></i>
                          <i class="far fa-circle" v-else></i>
                        </div>
                        <div class="lesson-content">
                          <div class="lesson-title-row">
                            <span class="lesson-title">{{ lesson.title }}</span>
                          </div>
                          <div class="lesson-meta">
                            <i class="fas fa-play-circle" v-if="lesson.contentType === 'YOUTUBE' || lesson.contentType === 'PANDA'"></i>
                            <i class="fas fa-file-pdf" v-if="lesson.contentType === 'PDF'"></i>
                            <i class="fas fa-question-circle" v-if="lesson.contentType === 'QUIZ'"></i>
                            <span class="lesson-type">{{ getLessonType(lesson.contentType) }}</span>
                          </div>
                        </div>
                      </button>
                    </template>
                  </div>
                </transition>
              </div>
            </template>

            <!-- Certificate Section -->
            <div class="module-section certificate-module" v-if="isCertificateAvailable && !isPreview">
              <button class="module-header certificate-header" @click="selectCertificate()">
                <div class="module-title-wrapper">
                  <i class="fas fa-trophy certificate-icon"></i>
                  <span class="module-title">Certificado</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="course-main-content">
        <div class="content-header">
          <h2>{{ currentLesson.title }}</h2>
        </div>

        <div class="content-body">
          <!-- Blocked Lesson Message -->
          <div v-if="selectedContent === 'blocked'" class="lesson-blocked-container">
            <div class="blocked-message">
              <div class="blocked-icon">
                <i class="fas fa-lock"></i>
              </div>
              <h3>Conteúdo Bloqueado</h3>
              <p class="blocked-reason">{{ lessonBlockedInfo?.blockedReason || 'Esta aula está bloqueada.' }}</p>
              <p class="blocked-instruction" v-if="lessonBlockedInfo?.requiredQuizLessonTitle">
                Complete o quiz "<strong>{{ lessonBlockedInfo.requiredQuizLessonTitle }}</strong>" para desbloquear este conteúdo.
              </p>
            </div>
          </div>

          <!-- Lesson Content -->
          <div v-show="selectedContent === 'lesson'" class="lesson-content">
            <!-- Quiz Component -->
            <div v-if="currentLesson.contentType === 'QUIZ'" class="quiz-container">
              <QuizPlayer
                :lesson="currentLesson"
                :course-id="course?.id"
                @quiz-completed="handleQuizCompleted"
              />
            </div>

            <!-- Video/PDF Content -->
            <div v-else class="media-container">
              <!-- YouTube Video -->
              <div v-if="currentLesson.contentType === 'YOUTUBE'" class="video-wrapper">
                <LiteYouTubeEmbed
                  :id="currentLesson.contentUrl?.split('=')[1]"
                  class="video-embed"
                  title="Vídeo da Lição"
                />
              </div>

              <!-- PDF Viewer -->
              <div v-else-if="currentLesson.contentType === 'PDF'" class="pdf-wrapper">
                <embed
                  :src="'https://docs.google.com/gview?embedded=true&url=' + currentLesson.contentUrl"
                  type="application/pdf"
                  width="100%"
                  height="100%"
                />
              </div>

              <!-- Panda Video -->
              <div v-else-if="currentLesson.contentType === 'PANDA'" class="video-wrapper">
                <iframe
                  :src="currentPandaVideo.contentUrl"
                  style="border:none;"
                  allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
                  allowfullscreen
                  width="100%"
                  height="100%"
                  fetchpriority="high"
                ></iframe>
              </div>
            </div>
          </div>

          <!-- Certificate Content -->
          <div v-if="selectedContent === 'certificate'" class="certificate-content">
            <iframe v-if="certificateUrl" :src="certificateUrl" frameborder="0"></iframe>
            <a :href="certificateUrl" download v-if="certificateUrl" class="btn-download-certificate">
              <i class="fa fa-download"></i> Baixar Certificado
            </a>
          </div>
        </div>
      </main>
    </div>

    <!-- Overlay for mobile sidebar -->
    <div class="sidebar-overlay" :class="{ 'active': sidebarOpen }" @click="toggleSidebar"></div>
  </div>
</template>

<script setup>
import ApiService from '@/services/ApiService';
import { tratarError } from '@/axiosConfig';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import LiteYouTubeEmbed from 'vue-lite-youtube-embed';
import { storeToRefs } from 'pinia';
import { useLoadinStore } from '@/stores/loadingStore';
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";
import QuizPlayer from '@/components/quiz/QuizPlayer.vue';
import { JWTHandler } from '@/jwtHandler';

const loadingStore = storeToRefs(useLoadinStore());
const route = useRoute();
const slug = route.params.slug;
const courseId = route.params.id;
const isPreview = !!courseId;

const studentName = ref('');
const course = ref(null);
const isCertificateAvailable = ref(false);
const currentLesson = ref({});
const currentModule = ref({});
const selectedContent = ref("lesson");
const certificateUrl = ref('');
const currentPandaVideo = ref({});
const sidebarOpen = ref(false);
const expandedModules = ref({});
const lessonBlockedInfo = ref(null);

onMounted(async () => {
  try {
    let response;
    if (isPreview) {
      response = await ApiService.get(`/courses/${courseId}/preview`);
      course.value = response.data.course;
      studentName.value = response.data.student || JWTHandler.getUserData()?.fullName || 'Preview';
      isCertificateAvailable.value = false;
    } else {
      response = await ApiService.get(`/enrollments/${slug}/course`);
      course.value = response.data.course;
      studentName.value = response.data.student;
      isCertificateAvailable.value = response.data.isCertificateAvailable;
    }

    if (course.value?.modules?.length > 0 && course.value.modules[0].lessons?.length > 0) {
      expandedModules.value[0] = true;
      selectLesson(course.value.modules[0], course.value.modules[0].lessons[0]);
    }
  } catch (error) {
    tratarError(error);
    console.error('Erro ao carregar curso:', error);
  }
});

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value;
};

const toggleModule = (index) => {
  expandedModules.value[index] = !expandedModules.value[index];
};

const checkLessonAccess = async (lesson) => {
  if (isPreview && lesson.contentType !== 'PANDA') {
    return {
      isBlocked: false,
      contentUrl: lesson.contentUrl,
    };
  }

  try {
    const response = await ApiService.get(`/lessons/${lesson.id}/panda`);
    return {
      isBlocked: response.data.isBlocked || false,
      blockedReason: response.data.blockedReason,
      requiredQuizLessonTitle: response.data.requiredQuizLessonTitle,
      contentUrl: response.data.contentUrl
    };
  } catch (error) {
    tratarError(error);
    console.error('Erro ao verificar acesso à lição:', error);
    return { isBlocked: false };
  }
};

const selectLesson = async (module, lesson) => {
  currentLesson.value = lesson;
  currentModule.value = module;
  selectedContent.value = 'lesson';

  if (window.innerWidth < 992) {
    sidebarOpen.value = false;
  }

  // Check lesson access for non-preview mode
  if (!isPreview && (lesson.contentType === 'YOUTUBE' || lesson.contentType === 'PDF' || lesson.contentType === 'QUIZ')) {
    const accessInfo = await checkLessonAccess(lesson);

    if (accessInfo.isBlocked) {
      lessonBlockedInfo.value = accessInfo;
      selectedContent.value = 'blocked';
      return;
    }

    lessonBlockedInfo.value = null;
  }

  if (lesson.contentType === 'PANDA') {
    await loadPandaVideo(lesson);
  }
};

const handleQuizCompleted = () => {
  console.log('Quiz completed');
};

const loadPandaVideo = async (lesson) => {
  currentPandaVideo.value = {};
  const accessInfo = await checkLessonAccess(lesson);

  if (accessInfo.isBlocked) {
    lessonBlockedInfo.value = accessInfo;
    selectedContent.value = 'blocked';
    return;
  }

  lessonBlockedInfo.value = null;
  currentPandaVideo.value = { contentUrl: accessInfo.contentUrl };
  selectedContent.value = 'lesson';
};

const getEnabledLessonsCount = (module) => {
  const count = module.lessons?.filter(l => l.isEnabled).length || 0;
  return count === 1 ? '1 aula' : `${count} aulas`;
};

const getLessonType = (contentType) => {
  const types = {
    'YOUTUBE': 'Vídeo',
    'PANDA': 'Vídeo',
    'PDF': 'Documento',
    'QUIZ': 'Quiz'
  };
  return types[contentType] || 'Conteúdo';
};

const selectCertificate = async () => {
  if (isPreview) {
    return;
  }
  try {
    const response = await ApiService.get(`/enrollments/certificate/${slug}`);
    certificateUrl.value = response.data;
  } catch (error) {
    tratarError(error);
    console.error('Erro ao carregar certificado:', error);
  }
  selectedContent.value = 'certificate';

  if (window.innerWidth < 992) {
    sidebarOpen.value = false;
  }
};

onBeforeUnmount(() => {
  // Cleanup if needed
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.course-layout-v3 {
  min-height: 100vh;
  background-color: #1c1d1f;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* Header */
.course-header {
  background-color: #2d2f31;
  color: #fff;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid #3e4143;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  max-width: 100%;
  height: 60px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.mobile-menu-toggle {
  display: none;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  margin-right: 0.5rem;
  transition: background-color 0.2s;
  border-radius: 4px;
}

.mobile-menu-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.course-info {
  min-width: 0;
  flex: 1;
}

.course-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-right {
  display: flex;
  align-items: center;
}

.btn-home {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: transparent;
  color: #fff;
  border: 1px solid #6a6f73;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-home:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-color: #fff;
}

/* Main Content Wrapper */
.course-content-wrapper {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Sidebar */
.course-sidebar {
  width: 380px;
  background-color: #f7f9fa;
  border-right: 1px solid #d1d7dc;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: calc(100vh - 60px);
}

.sidebar-header {
  padding: 1rem 1.5rem;
  background-color: #fff;
  border-bottom: 1px solid #d1d7dc;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sidebar-header h3 {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
  color: #1c1d1f;
}

.close-sidebar {
  display: none;
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #1c1d1f;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.close-sidebar:hover {
  background-color: #e3e7ea;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Modules List */
.modules-list {
  padding: 0;
}

.module-section {
  background-color: #fff;
  border-bottom: 1px solid #d1d7dc;
}

.module-header {
  width: 100%;
  padding: 1rem 1.5rem;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.15s;
  text-align: left;
}

.module-header:hover {
  background-color: #f7f9fa;
}

.module-title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.module-chevron {
  font-size: 0.75rem;
  color: #1c1d1f;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.module-chevron.expanded {
  transform: rotate(-180deg);
}

.module-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1c1d1f;
  line-height: 1.4;
}

.module-lesson-count {
  font-size: 0.875rem;
  color: #6a6f73;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

/* Lessons Container */
.lessons-container {
  background-color: #f7f9fa;
  padding: 0.5rem 0;
}

.lesson-button {
  width: 100%;
  padding: 0.75rem 1.5rem 0.75rem 3rem;
  background-color: transparent;
  border: none;
  border-left: 4px solid transparent;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  text-align: left;
  transition: all 0.15s;
}

.lesson-button:hover {
  background-color: #e3e7ea;
}

.lesson-button.active {
  background-color: #d1e7dd;
  border-left-color: #0d6efd;
}

.lesson-checkbox {
  flex-shrink: 0;
  font-size: 1rem;
  color: #6a6f73;
  margin-top: 0.125rem;
}

.lesson-button.active .lesson-checkbox {
  color: #0d6efd;
}

.lesson-checkbox i {
  display: block;
}

.lesson-content {
  flex: 1;
  min-width: 0;
}

.lesson-title-row {
  margin-bottom: 0.25rem;
}

.lesson-title {
  font-size: 0.875rem;
  font-weight: 400;
  color: #1c1d1f;
  line-height: 1.4;
  display: block;
}

.lesson-button.active .lesson-title {
  font-weight: 600;
  color: #1c1d1f;
}

.lesson-meta {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.25rem;
}

.lesson-meta i {
  font-size: 0.75rem;
  color: #6a6f73;
}

.lesson-type {
  font-size: 0.75rem;
  color: #6a6f73;
}

/* Certificate Section */
.certificate-module {
  background-color: #fff;
}

.certificate-header {
  background-color: #fef5e7;
}

.certificate-header:hover {
  background-color: #fdebd0;
}

.certificate-icon {
  color: #f39c12;
  font-size: 1rem;
}

/* Main Content */
.course-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background-color: #1c1d1f;
}

.content-header {
  background-color: #1c1d1f;
  color: #fff;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #3e4143;
}

.content-header h2 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
}

.content-body {
  flex: 1;
  background-color: #1c1d1f;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0;
  overflow-y: auto;
}

.lesson-content {
  width: 100%;
  min-height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem;
}

.lesson-content .quiz-container {
  margin-top: 0;
}

.lesson-content .media-container {
  width: 100%;
  max-width: 1400px;
}

.certificate-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

/* Media Containers */
.quiz-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: #fff;
  border-radius: 8px;
}

.media-container {
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background-color: transparent;
}

.video-wrapper,
.pdf-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background-color: #000;
  overflow: hidden;
}

.video-wrapper iframe,
.video-wrapper .video-embed,
.pdf-wrapper embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Certificate Content */
.certificate-content iframe {
  width: 100%;
  max-width: 1200px;
  min-height: 600px;
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background-color: #fff;
  margin-bottom: 1.5rem;
}

.btn-download-certificate {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  background-color: #0d6efd;
  color: #fff;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-download-certificate:hover {
  background-color: #0b5ed7;
  color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

/* Sidebar Overlay */
.sidebar-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  transition: opacity 0.3s;
}

.sidebar-overlay.active {
  opacity: 1;
}

/* Transitions */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 991px) {
  .mobile-menu-toggle {
    display: block;
  }

  .btn-text {
    display: none;
  }

  .course-sidebar {
    position: fixed;
    top: 60px;
    left: -380px;
    width: 380px;
    height: calc(100vh - 60px);
    z-index: 1000;
    transition: left 0.3s ease;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  }

  .course-sidebar.mobile-open {
    left: 0;
  }

  .close-sidebar {
    display: block;
  }

  .sidebar-overlay {
    display: block;
  }

  .course-title {
    font-size: 0.9rem;
  }

  .content-header h2 {
    font-size: 1.25rem;
  }

  .quiz-container,
  .media-container {
    padding: 1rem;
  }
}

@media (max-width: 767px) {
  .course-sidebar {
    width: 85%;
    left: -85%;
  }

  .header-content {
    padding: 0.75rem 1rem;
  }

  .course-title {
    font-size: 0.85rem;
  }

  .content-header {
    padding: 1rem 1.5rem;
  }

  .content-header h2 {
    font-size: 1.125rem;
  }

  .certificate-content {
    padding: 1rem;
  }

  .module-title {
    font-size: 0.9rem;
  }

  .lesson-title {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 0.5rem 0.75rem;
  }

  .course-title {
    font-size: 0.8rem;
  }

  .content-header h2 {
    font-size: 1rem;
  }

  .quiz-container,
  .media-container {
    padding: 0.5rem;
  }

  .module-header {
    padding: 0.875rem 1rem;
  }

  .lesson-button {
    padding: 0.625rem 1rem 0.625rem 2.5rem;
  }
}

/* Blocked Lesson Styles */
.lesson-blocked-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: #1c1d1f;
}

.blocked-message {
  text-align: center;
  max-width: 500px;
  background-color: #fff;
  padding: 40px 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.blocked-icon {
  font-size: 72px;
  color: #f39c12;
  margin-bottom: 20px;
}

.blocked-message h3 {
  font-size: 24px;
  font-weight: 700;
  color: #1c1d1f;
  margin-bottom: 15px;
}

.blocked-reason {
  font-size: 16px;
  color: #6a6f73;
  margin-bottom: 10px;
  line-height: 1.5;
}

.blocked-instruction {
  font-size: 16px;
  color: #1c1d1f;
  padding: 15px 20px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  margin-top: 20px;
  line-height: 1.6;
}

.blocked-instruction strong {
  color: #129f46;
  font-weight: 600;
}
</style>
